import { ArrowLeft, Users01 } from '@untitledui/icons';
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  GoogleBrandMark,
  MicrosoftBrandMark,
} from '@/components/integrations/ProviderBrandMarks';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { CARD_BORDER } from '@/lib/base/list-card';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { AlertFullWidth } from '@/lib/base/overlays/alerts';
import { getApiBaseUrl } from '@/lib/config';
import { fetchWithDefaults } from '@/lib/http/client';
import { useViewHealthMarker } from '@/lib/view-health';
import { cx } from '@/utils/cx';
import { humanize } from '@/utils/text';

import { useConfigInfo } from '../../allauth_auth';
import { useOrg } from '../../contexts/OrgContext';
import { useTitle } from '../../hooks/useTitle';
import { redirectToProvider } from '../../lib/allauth';
import type { Provider } from '../../types/allauth';

interface SocialAccount {
  id: number;
  provider: string;
  uid: string;
  extra_data: {
    email?: string;
    name?: string;
    login?: string;
    screen_name?: string;
    org_id?: number;
    scope?: string;
  };
}

interface SocialConnectionsData {
  accounts: SocialAccount[];
  available_providers: Provider[];
}

const API_BASE_URL = getApiBaseUrl();

/**
 * Inline brand marks, preferred over the Django `static/images/socialauth/`
 * assets: we ship art for the two providers that matter here, and using the
 * shared components keeps Google and Microsoft identical to how they render on
 * the Email & Calendar cards. Microsoft in particular used to fall through to
 * the monogram below, so the same account appeared as a four-square logo on one
 * settings page and a grey "M" tile on the next.
 */
const PROVIDER_BRAND_MARKS: Record<string, () => ReactElement> = {
  google: () => <GoogleBrandMark className='size-5 shrink-0' />,
  microsoft: () => <MicrosoftBrandMark className='size-5 shrink-0' />,
};

/** Providers we have a Django-served logo for but no inline mark. */
const PROVIDER_LOGO_FILES: Record<string, string> = {};

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

/**
 * A provider's brand mark.
 *
 * Providers we have no art for fall back to a monogram rather than a generic
 * "group of people" glyph — that glyph is also this page's empty-state icon, so
 * a provider using it looked like a rendering bug. A monogram is distinct per
 * provider and is never wrong.
 */
function ProviderLogo({
  providerId,
  name,
}: {
  providerId: string;
  name: string;
}) {
  const BrandMark = PROVIDER_BRAND_MARKS[providerId];
  if (BrandMark) {
    return <BrandMark />;
  }
  const logoFile = PROVIDER_LOGO_FILES[providerId];
  if (logoFile) {
    return (
      <img
        src={`${API_BASE_URL}/static/images/socialauth/${logoFile}`}
        alt=''
        aria-hidden='true'
        className='size-5 shrink-0'
      />
    );
  }
  return (
    <span
      aria-hidden='true'
      className='flex size-5 shrink-0 items-center justify-center rounded bg-bg-tertiary text-[10px] font-semibold text-fg-secondary uppercase'
    >
      {name.charAt(0)}
    </span>
  );
}

function formatAccountInfo(account: SocialAccount): string {
  switch (account.provider) {
    case 'google':
      return account.extra_data.email || 'Connected';
    case 'github':
      return account.extra_data.login
        ? `@${account.extra_data.login}`
        : 'Connected';
    case 'twitter':
      return account.extra_data.screen_name
        ? `@${account.extra_data.screen_name}`
        : 'Connected';
    default:
      return account.extra_data.email || account.extra_data.name || 'Connected';
  }
}

/** One connected account: who it is on the left, its state and action on the right. */
function ConnectedAccountRow({
  account,
  isDisconnecting,
  onDisconnect,
}: {
  account: SocialAccount;
  isDisconnecting: boolean;
  onDisconnect: (accountId: number) => void;
}) {
  const handleDisconnect = useCallback(() => {
    onDisconnect(account.id);
  }, [onDisconnect, account.id]);

  return (
    <div className='flex items-center justify-between gap-4 px-5 py-4'>
      <div className='flex min-w-0 items-center gap-3'>
        <ProviderLogo providerId={account.provider} name={account.provider} />
        <div className='min-w-0'>
          <p className='truncate text-sm font-medium text-fg-primary'>
            {PROVIDER_LABELS[account.provider] ?? humanize(account.provider)}
          </p>
          <p className='truncate text-sm text-fg-tertiary'>
            {formatAccountInfo(account)}
          </p>
        </div>
      </div>
      <div className='flex shrink-0 items-center gap-3'>
        <Badge type='color' size='sm' color='success'>
          Connected
        </Badge>
        <Button
          color='secondary'
          size='sm'
          onClick={handleDisconnect}
          isDisabled={isDisconnecting}
        >
          {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
        </Button>
      </div>
    </div>
  );
}

/**
 * One connectable provider, as a row rather than a full-width button-card.
 *
 * Only rendered for providers that are NOT already connected to this team —
 * a connected provider is represented by its ConnectedAccountRow above, which
 * already carries the Connected badge and the Disconnect action. Rendering
 * both listed the same provider twice with two "Connected" badges.
 */
function ProviderRow({
  provider,
  onConnect,
}: {
  provider: Provider;
  onConnect: (provider: Provider) => void;
}) {
  const handleConnect = useCallback(() => {
    onConnect(provider);
  }, [onConnect, provider]);

  return (
    <div className='flex items-center justify-between gap-4 px-5 py-4'>
      <div className='flex min-w-0 items-center gap-3'>
        <ProviderLogo providerId={provider.id} name={provider.name} />
        <p className='truncate text-sm font-medium text-fg-primary'>
          {provider.name}
        </p>
      </div>
      <Button color='secondary' size='sm' onClick={handleConnect}>
        Connect {provider.name}
      </Button>
    </div>
  );
}

export default function SocialConnections() {
  const { providers: configProviders } = useConfigInfo();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();

  useTitle('Connected Accounts');
  // Rendered by SettingsLayout under /settings/:section, whose route health
  // handle waits on this marker — without it the route never reports ready.
  useViewHealthMarker('settings-section-ready');

  const [data, setData] = useState<SocialConnectionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<number | null>(null);
  const [accountToDisconnect, setAccountToDisconnect] = useState<number | null>(
    null
  );

  const loadConnectionsData = useCallback(async () => {
    // Wait for currentOrg to be available
    if (!currentOrg?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithDefaults(
        `/api/orgs/${currentOrg.id}/social-connections/`
      );

      if (!response.ok) {
        throw new Error('Failed to load social connections');
      }

      const responseData = await response.json();

      // Get available providers from allauth config
      const availableProviders =
        configProviders.filter((p: Provider) => p.client_id) || [];

      setData({
        accounts: responseData.accounts || [],
        available_providers: availableProviders,
      });
    } catch (err) {
      console.error('Error loading social connections:', err);
      setError("We couldn't load your social connections. Try again.");
    } finally {
      setLoading(false);
    }
  }, [configProviders, currentOrg]);

  useEffect(() => {
    loadConnectionsData();
  }, [loadConnectionsData]);

  const handleDisconnectClick = useCallback((accountId: number) => {
    setAccountToDisconnect(accountId);
  }, []);

  const handleConfirmDisconnect = useCallback(async () => {
    if (accountToDisconnect === null) {
      return;
    }
    try {
      setDisconnecting(accountToDisconnect);
      setError(null);
      const response = await fetchWithDefaults(
        `/users/api/social-connections/disconnect/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            account_id: accountToDisconnect,
            org_id: currentOrg?.id,
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disconnect account');
      }
      await loadConnectionsData();
      setAccountToDisconnect(null);
    } catch (err) {
      console.error('Error disconnecting account:', err);
      setError("We couldn't disconnect that account. Try again.");
      throw err; // re-throw for ConfirmationDialog to stay open
    } finally {
      setDisconnecting(null);
    }
  }, [accountToDisconnect, currentOrg?.id, loadConnectionsData]);

  const handleCancelDisconnect = useCallback(() => {
    setAccountToDisconnect(null);
  }, []);

  const handleDismissError = useCallback(() => {
    setError(null);
    loadConnectionsData();
  }, [loadConnectionsData]);

  /**
   * Return to wherever the user actually came from.
   *
   * This page is reached from several places (Settings → Email & calendar,
   * Profile, and the OAuth callback), so the old hardcoded "Back to Profile"
   * sent people somewhere they had never been. React Router only assigns the
   * key 'default' to the first entry in the session's history stack, so a
   * different key means there is a real previous page to pop back to; when
   * there isn't one (direct load, or landing here after an OAuth redirect),
   * fall back to Email & calendar — the section that links here.
   */
  const handleBack = useCallback(() => {
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    navigate(`/${currentOrg?.id}/settings/email-calendar`);
  }, [location.key, navigate, currentOrg?.id]);

  const handleConnect = useCallback(
    async (provider: Provider) => {
      if (!currentOrg) {
        setError('No team selected');
        return;
      }

      try {
        // Call our team-scoped endpoint to store org_id in session
        const response = await fetchWithDefaults(
          `/users/api/social-connections/connect-for-team/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              org_id: currentOrg.id,
              provider: provider.id,
            }),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to initiate connection');
        }

        // After storing org_id in session, redirect to OAuth
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- OAuth error response structure varies by provider
        (redirectToProvider as any)(
          provider.id,
          '/account/provider/callback',
          'connect'
        );
      } catch (err) {
        console.error('Error initiating connection:', err);
        setError("We couldn't connect that account. Try again.");
      }
    },
    [currentOrg]
  );

  if (loading) {
    return (
      <div className='flex justify-center py-12'>
        <div className='text-center'>
          <LoadingIndicator size='md' />
          <p className='text-sm text-tertiary'>Loading connected accounts…</p>
        </div>
      </div>
    );
  }

  // Rendered as a settings section, so the surrounding page chrome (background,
  // max-width, padding) comes from SettingsLayout. It used to be its own
  // top-level route and supplied all of that itself, which is why it appeared
  // without the settings sidebar — as if the user had been thrown out of
  // settings by following a link inside them.
  return (
    <div className='space-y-6'>
      <div className='space-y-6'>
        {/* Back points at Email & Calendar, the only page that links here, when
            there is no real history to return to. There used to be a
            "Profile >" breadcrumb AND a "Back to Profile" button at the foot,
            both asserting a parent this page does not have. */}
        <Button
          color='link-gray'
          size='sm'
          iconLeading={ArrowLeft}
          onClick={handleBack}
          className='self-start'
        >
          Back
        </Button>

        {error && (
          <AlertFullWidth
            color='error'
            title='Unable to disconnect account'
            description={error}
            onClose={handleDismissError}
          />
        )}

        <div>
          <h2 className='text-lg font-semibold text-fg-primary'>
            Connected accounts
          </h2>
          <p className='text-sm text-fg-tertiary mt-0.5'>
            Sign in to Ahoy with these accounts, and let Ahoy act on your behalf
            in them.
          </p>
        </div>

        {/* One card, one row per provider. Previously an "Add Connection" card
            of full-width centred buttons sat above a separate "No Connected
            Accounts" empty-state card, which restated the same thing a second
            time and made an empty page three cards tall. */}
        {data && data.available_providers.length > 0 ? (
          <div className={cx(CARD_BORDER, 'divide-y divide-border-secondary')}>
            {data.accounts.map(account => (
              <ConnectedAccountRow
                key={account.id}
                account={account}
                isDisconnecting={disconnecting === account.id}
                onDisconnect={handleDisconnectClick}
              />
            ))}
            {data.available_providers
              .filter(
                provider =>
                  !data.accounts.some(
                    account =>
                      account.provider === provider.id &&
                      account.extra_data.org_id === currentOrg?.id
                  )
              )
              .map(provider => (
                <ProviderRow
                  key={provider.id}
                  provider={provider}
                  onConnect={handleConnect}
                />
              ))}
          </div>
        ) : (
          <div className={cx(CARD_BORDER, 'px-6 py-10 text-center')}>
            <Users01
              className='mx-auto size-6 text-fg-quaternary'
              aria-hidden='true'
            />
            <h3 className='mt-3 text-sm font-semibold text-fg-primary'>
              No providers available
            </h3>
            <p className='mt-1 text-sm text-fg-tertiary'>
              No additional social login providers are configured.
            </p>
          </div>
        )}

        {error?.includes('set a password') && (
          <p className='text-sm text-fg-tertiary'>
            You need a password before this account can be disconnected.{' '}
            <Link
              to='/account/password/change'
              className='font-medium text-fg-brand-primary underline'
            >
              Set a password
            </Link>
          </p>
        )}
      </div>
      <ConfirmationDialog
        isOpen={accountToDisconnect !== null}
        variant='warning'
        title='Disconnect account'
        description='This will only affect the current team. You can reconnect any time.'
        confirmLabel='Disconnect'
        isLoading={disconnecting !== null}
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
      />
    </div>
  );
}
